import { useMemo, useState } from "react"
import { Download } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { FilterChip } from "@/components/filter-chip"
import { FilterToolbar } from "@/components/filter-toolbar"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { LotsTable } from "@/features/lots/components/lots-table"
import { useLots } from "@/features/lots/hooks/use-lots"
import { lotNetMT } from "@/features/lots/lib/lot-math"
import type { ListLotsParams, LotStatus } from "@/features/lots/api/types"
import { useUsinages } from "@/features/milling/hooks/use-milling"

const STATUS_OPTIONS: LotStatus[] = [
  "IN_PROGRESS",
  "COMPLETED",
  "RECONDITIONED",
]

export function LotsPage() {
  const { t } = useTranslation("lots")
  const [search, setSearch] = useState("")
  const [usinageFilter, setUsinageFilter] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const apiParams = useMemo<ListLotsParams>(() => {
    const out: ListLotsParams = {
      // The warehouse list needs client + certifications + active OT
      // per row; the backend opts in via this flag. `includeQc` adds
      // the per-lot QC log embed so the table can show whether lab /
      // client analyses have been recorded.
      includeContext: true,
      includeQc: true,
    }
    if (search.trim()) out.q = search.trim()
    if (usinageFilter !== "all") out.usinageId = usinageFilter
    if (statusFilter !== "all") out.status = statusFilter as LotStatus
    return out
  }, [search, usinageFilter, statusFilter])

  const { data: lots, isLoading } = useLots(apiParams)
  const { data: usinages } = useUsinages("all")

  const list = lots ?? []
  const totalMT = list.reduce((a, l) => a + lotNetMT(l), 0)
  const activeFilters =
    (usinageFilter !== "all" ? 1 : 0) + (statusFilter !== "all" ? 1 : 0)
  const hasFilters = activeFilters > 0 || search.trim() !== ""

  const usinageOptions = (usinages ?? []).map((u) => ({
    value: String(u.id),
    label: u.usinageNumber,
  }))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t("list.eyebrow")}
        title={t("list.title")}
        description={t("list.subtitle", {
          filtered: list.length,
          total: list.length,
          mt: totalMT.toFixed(2),
        })}
        actions={
          <Button variant="outline" size="sm">
            <Download className="size-4" />
            {t("list.export")}
          </Button>
        }
      />

      <Card className="p-3">
        <FilterToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("list.searchPlaceholder")}
          activeFilterCount={activeFilters}
          onReset={() => {
            setUsinageFilter("all")
            setStatusFilter("all")
          }}
          resetLabel={t("list.clear")}
        >
          <FilterChip
            label={t("list.filterUsinage")}
            value={usinageFilter}
            onValueChange={setUsinageFilter}
            options={usinageOptions}
            allLabel={t("list.filterAllUsinages")}
          />
          <FilterChip
            label={t("list.filterStatus")}
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={STATUS_OPTIONS.map((s) => ({
              value: s,
              label: t(`status.${s}`),
            }))}
            allLabel={t("list.filterAllStatuses")}
          />
        </FilterToolbar>
      </Card>

      {isLoading ? (
        <LoadingState />
      ) : list.length === 0 ? (
        <EmptyState
          title={
            hasFilters ? t("list.emptyFiltered") : t("list.emptyAll")
          }
        />
      ) : (
        <LotsTable lots={list} />
      )}
    </div>
  )
}
